import { useState } from "react";
import WorldMap, { HUBS } from "@/components/WorldMap";
import SectionLabel from "@/components/SectionLabel";
import { Globe2, Radar, ShieldCheck } from "lucide-react";

const regions = [
  { name: "North America", count: 12, hubs: ["New York", "Toronto", "Mexico City"], routes: 34 },
  { name: "Europe", count: 18, hubs: ["London", "Frankfurt", "Zurich"], routes: 62 },
  { name: "Middle East", count: 7, hubs: ["Dubai"], routes: 28 },
  { name: "Asia Pacific", count: 21, hubs: ["Singapore", "Hong Kong", "Tokyo", "Mumbai"], routes: 71 },
  { name: "Africa", count: 8, hubs: ["Johannesburg"], routes: 19 },
  { name: "South America", count: 6, hubs: ["São Paulo"], routes: 14 },
  { name: "Oceania", count: 3, hubs: ["Sydney"], routes: 9 },
];

export default function Coverage() {
  const [activeRegion, setActiveRegion] = useState(null);
  const active = regions.find(r => r.name === activeRegion);
  const activeHubs = active ? active.hubs : [];

  return (
    <div className="relative">
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 pt-20 pb-10">
        <SectionLabel>Reach · 84 Jurisdictions</SectionLabel>
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <h1 className="lg:col-span-8 font-display font-black uppercase tracking-tight text-5xl sm:text-6xl text-[#F0F4F8] leading-[0.95]">
            One command room.<br />
            <span className="text-[#D4AF37]">Every continent.</span>
          </h1>
          <p className="lg:col-span-4 text-[#9EABC0] leading-relaxed">
            AEGIS operates 47 command centers, 84 sovereign licenses and 300+ vetted armored vehicles.
            Every route below is active as of this hour.
          </p>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Region list */}
          <aside className="lg:col-span-3 space-y-2" data-testid="region-list">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Regions</div>
            {regions.map((r) => (
              <button
                key={r.name}
                onClick={() => setActiveRegion(activeRegion === r.name ? null : r.name)}
                data-testid={`region-${r.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`w-full text-left px-4 py-4 border transition-all ${
                  activeRegion === r.name
                    ? "border-[#D4AF37] bg-[#D4AF37]/10"
                    : "border-[#A1AEC2]/20 hover:border-[#D4AF37]/50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-display text-lg font-semibold text-[#F0F4F8]">{r.name}</span>
                  <span className="font-mono text-xs text-[#D4AF37]">{r.count}</span>
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9EABC0]">
                  {r.routes} live routes
                </div>
              </button>
            ))}
          </aside>

          {/* Map */}
          <div className="lg:col-span-9 relative border border-[#A1AEC2]/20 bg-[#0A1024] p-4">
            <div className="absolute top-4 left-4 z-10 flex items-center gap-3">
              <Radar className="w-4 h-4 text-[#D4AF37]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                Live Operations Grid · {new Date().toISOString().slice(0,10)}
              </span>
            </div>
            <div className="absolute top-4 right-4 z-10 flex gap-2 items-center">
              <span className="w-2 h-2 bg-[#D4AF37] aegis-pulse rounded-full" />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#9EABC0]">Synced</span>
            </div>
            <WorldMap activeHubs={activeHubs} />
            {active && (
              <div className="mt-4 border-t border-[#A1AEC2]/15 pt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9EABC0]">Region</div>
                  <div className="mt-1 font-display text-lg text-[#F0F4F8]">{active.name}</div>
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9EABC0]">Nations</div>
                  <div className="mt-1 font-display text-lg text-[#D4AF37]">{active.count}</div>
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9EABC0]">Live Routes</div>
                  <div className="mt-1 font-display text-lg text-[#D4AF37]">{active.routes}</div>
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9EABC0]">Primary Hubs</div>
                  <div className="mt-1 font-mono text-xs text-[#F0F4F8]">{active.hubs.join(" · ")}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Hubs table */}
        <div className="mt-16">
          <SectionLabel>Command Hubs</SectionLabel>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-[#A1AEC2]/15 border border-[#A1AEC2]/15">
            {HUBS.map((h) => (
              <div key={h.name} className="bg-[#060C1F] p-4">
                <Globe2 className="w-4 h-4 text-[#D4AF37]" />
                <div className="mt-3 font-display text-sm font-semibold text-[#F0F4F8]">{h.name}</div>
                <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#9EABC0]">
                  {h.lat.toFixed(1)}°, {h.lon.toFixed(1)}°
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Compliance strip */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            ["ISO 27001", "Information Security"],
            ["TAPA Class A", "Facility Security"],
            ["C-TPAT", "US Customs Partner"],
            ["IATA CEIV", "Air Cargo Certified"],
          ].map(([t, d]) => (
            <div key={t} className="border border-[#A1AEC2]/20 p-6 flex items-start gap-4 hover:border-[#D4AF37]/60">
              <ShieldCheck className="w-6 h-6 text-[#D4AF37] flex-shrink-0" />
              <div>
                <div className="font-display font-bold text-[#F0F4F8]">{t}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9EABC0] mt-1">{d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
